import {
  createContext,
  useContext,
  useReducer,
  useCallback,
  useEffect,
  useMemo,
  ReactNode,
} from 'react';
import type {
  Tour,
  TourStep,
  TourState,
  TourContextValue,
  TourProgress,
} from './types';
import { TourOverlay } from './TourOverlay';

const STORAGE_KEY = 'ohcs_tour_progress';

// Action types
type TourAction =
  | { type: 'START_TOUR'; tour: Tour }
  | { type: 'NEXT_STEP' }
  | { type: 'PREV_STEP' }
  | { type: 'GO_TO_STEP'; index: number }
  | { type: 'SKIP_TOUR' }
  | { type: 'COMPLETE_TOUR' }
  | { type: 'PAUSE_TOUR' }
  | { type: 'RESUME_TOUR' }
  | { type: 'SET_PROGRESS'; progress: Record<string, TourProgress> }
  | { type: 'RESET_TOUR_PROGRESS'; tourId: string }
  | { type: 'RESET_ALL_PROGRESS' }
  | { type: 'REGISTER_TOUR'; tour: Tour };

// Initial state
const initialState: TourState = {
  activeTour: null,
  currentStepIndex: 0,
  isVisible: false,
  isPaused: false,
  progress: {},
};

// Registered tours storage
let registeredTours: Map<string, Tour> = new Map();

// Reducer
function tourReducer(state: TourState, action: TourAction): TourState {
  switch (action.type) {
    case 'START_TOUR':
      return {
        ...state,
        activeTour: action.tour,
        currentStepIndex: 0,
        isVisible: true,
        isPaused: false,
      };

    case 'NEXT_STEP':
      if (!state.activeTour) return state;
      const nextIndex = state.currentStepIndex + 1;
      if (nextIndex >= state.activeTour.steps.length) {
        return state; // Don't advance past end
      }
      return {
        ...state,
        currentStepIndex: nextIndex,
      };

    case 'PREV_STEP':
      if (!state.activeTour) return state;
      return {
        ...state,
        currentStepIndex: Math.max(0, state.currentStepIndex - 1),
      };

    case 'GO_TO_STEP':
      if (!state.activeTour) return state;
      return {
        ...state,
        currentStepIndex: Math.max(
          0,
          Math.min(action.index, state.activeTour.steps.length - 1)
        ),
      };

    case 'SKIP_TOUR':
      if (!state.activeTour) return state;
      return {
        ...state,
        activeTour: null,
        currentStepIndex: 0,
        isVisible: false,
        isPaused: false,
        progress: {
          ...state.progress,
          [state.activeTour.id]: {
            tourId: state.activeTour.id,
            completed: false,
            skipped: true,
            lastStepIndex: state.currentStepIndex,
            timestamp: Date.now(),
          },
        },
      };

    case 'COMPLETE_TOUR':
      if (!state.activeTour) return state;
      return {
        ...state,
        activeTour: null,
        currentStepIndex: 0,
        isVisible: false,
        isPaused: false,
        progress: {
          ...state.progress,
          [state.activeTour.id]: {
            tourId: state.activeTour.id,
            completed: true,
            skipped: false,
            lastStepIndex: state.activeTour.steps.length - 1,
            timestamp: Date.now(),
          },
        },
      };

    case 'PAUSE_TOUR':
      return {
        ...state,
        isVisible: false,
        isPaused: true,
      };

    case 'RESUME_TOUR':
      if (!state.activeTour) return state;
      return {
        ...state,
        isVisible: true,
        isPaused: false,
      };

    case 'SET_PROGRESS':
      return {
        ...state,
        progress: action.progress,
      };

    case 'RESET_TOUR_PROGRESS': {
      const { [action.tourId]: _, ...restProgress } = state.progress;
      return {
        ...state,
        progress: restProgress,
      };
    }

    case 'RESET_ALL_PROGRESS':
      return {
        ...state,
        progress: {},
      };

    case 'REGISTER_TOUR':
      registeredTours.set(action.tour.id, action.tour);
      return state;

    default:
      return state;
  }
}

// Context
const TourContext = createContext<TourContextValue | null>(null);

// Provider component
interface TourProviderProps {
  children: ReactNode;
}

export function TourProvider({ children }: TourProviderProps) {
  const [state, dispatch] = useReducer(tourReducer, initialState);

  // Load progress from localStorage on mount
  useEffect(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const progress = JSON.parse(stored) as Record<string, TourProgress>;
        dispatch({ type: 'SET_PROGRESS', progress });
      }
    } catch (e) {
      console.warn('Failed to load tour progress:', e);
    }
  }, []);

  // Save progress to localStorage when it changes
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(state.progress));
    } catch (e) {
      console.warn('Failed to save tour progress:', e);
    }
  }, [state.progress]);

  // Handle step enter/leave callbacks
  useEffect(() => {
    if (!state.activeTour || !state.isVisible) return;

    const currentStep = state.activeTour.steps[state.currentStepIndex];
    if (currentStep?.onEnter) {
      currentStep.onEnter();
    }

    return () => {
      if (currentStep?.onLeave) {
        currentStep.onLeave();
      }
    };
  }, [state.activeTour, state.currentStepIndex, state.isVisible]);

  // Keyboard navigation
  useEffect(() => {
    if (!state.isVisible || !state.activeTour) return;

    function handleKeyDown(e: KeyboardEvent) {
      switch (e.key) {
        case 'Escape':
          dispatch({ type: 'SKIP_TOUR' });
          state.activeTour?.onSkip?.();
          break;
        case 'ArrowRight':
        case 'Enter':
          if (state.currentStepIndex < state.activeTour!.steps.length - 1) {
            dispatch({ type: 'NEXT_STEP' });
          } else {
            dispatch({ type: 'COMPLETE_TOUR' });
            state.activeTour?.onComplete?.();
          }
          break;
        case 'ArrowLeft':
          dispatch({ type: 'PREV_STEP' });
          break;
      }
    }

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [state.isVisible, state.activeTour, state.currentStepIndex]);

  // Context methods
  const startTour = useCallback((tourId: string) => {
    const tour = registeredTours.get(tourId);
    if (tour) {
      tour.onStart?.();
      dispatch({ type: 'START_TOUR', tour });
    } else {
      console.warn(`Tour "${tourId}" not found`);
    }
  }, []);

  const nextStep = useCallback(() => {
    if (!state.activeTour) return;
    if (state.currentStepIndex >= state.activeTour.steps.length - 1) {
      dispatch({ type: 'COMPLETE_TOUR' });
      state.activeTour.onComplete?.();
    } else {
      dispatch({ type: 'NEXT_STEP' });
    }
  }, [state.activeTour, state.currentStepIndex]);

  const prevStep = useCallback(() => {
    dispatch({ type: 'PREV_STEP' });
  }, []);

  const goToStep = useCallback((index: number) => {
    dispatch({ type: 'GO_TO_STEP', index });
  }, []);

  const skipTour = useCallback(() => {
    if (state.activeTour) {
      state.activeTour.onSkip?.();
    }
    dispatch({ type: 'SKIP_TOUR' });
  }, [state.activeTour]);

  const completeTour = useCallback(() => {
    if (state.activeTour) {
      state.activeTour.onComplete?.();
    }
    dispatch({ type: 'COMPLETE_TOUR' });
  }, [state.activeTour]);

  const pauseTour = useCallback(() => {
    dispatch({ type: 'PAUSE_TOUR' });
  }, []);

  const resumeTour = useCallback(() => {
    dispatch({ type: 'RESUME_TOUR' });
  }, []);

  const isTourCompleted = useCallback(
    (tourId: string) => {
      return state.progress[tourId]?.completed ?? false;
    },
    [state.progress]
  );

  const isTourSkipped = useCallback(
    (tourId: string) => {
      return state.progress[tourId]?.skipped ?? false;
    },
    [state.progress]
  );

  const resetTourProgress = useCallback((tourId: string) => {
    dispatch({ type: 'RESET_TOUR_PROGRESS', tourId });
  }, []);

  const resetAllProgress = useCallback(() => {
    dispatch({ type: 'RESET_ALL_PROGRESS' });
  }, []);

  const currentStep: TourStep | null = useMemo(() => {
    if (!state.activeTour) return null;
    return state.activeTour.steps[state.currentStepIndex] ?? null;
  }, [state.activeTour, state.currentStepIndex]);

  const totalSteps = state.activeTour?.steps.length ?? 0;

  const registerTour = useCallback((tour: Tour) => {
    dispatch({ type: 'REGISTER_TOUR', tour });
  }, []);

  const getAvailableTours = useCallback(() => {
    return Array.from(registeredTours.values());
  }, []);

  const value: TourContextValue = useMemo(
    () => ({
      ...state,
      startTour,
      nextStep,
      prevStep,
      goToStep,
      skipTour,
      completeTour,
      pauseTour,
      resumeTour,
      isTourCompleted,
      isTourSkipped,
      resetTourProgress,
      resetAllProgress,
      currentStep,
      totalSteps,
      registerTour,
      getAvailableTours,
    }),
    [
      state,
      startTour,
      nextStep,
      prevStep,
      goToStep,
      skipTour,
      completeTour,
      pauseTour,
      resumeTour,
      isTourCompleted,
      isTourSkipped,
      resetTourProgress,
      resetAllProgress,
      currentStep,
      totalSteps,
      registerTour,
      getAvailableTours,
    ]
  );

  return (
    <TourContext.Provider value={value}>
      {children}
      {state.isVisible && state.activeTour && currentStep && (
        <TourOverlay
          step={currentStep}
          stepIndex={state.currentStepIndex}
          totalSteps={totalSteps}
          onNext={nextStep}
          onPrev={prevStep}
          onSkip={skipTour}
          showProgress={state.activeTour.showProgress !== false}
        />
      )}
    </TourContext.Provider>
  );
}

// Hook to use tour context
export function useTour(): TourContextValue {
  const context = useContext(TourContext);
  if (!context) {
    throw new Error('useTour must be used within a TourProvider');
  }
  return context;
}
